import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Note, Category, NoteRequest, CategoryRequest, TodoItem } from "../../models/note";

const LOCAL_NOTES_KEY = "NOTES";
const LOCAL_CATEGORIES_KEY = "CATEGORIES";

export interface NotesModalData {
  visible: boolean;
  semesterId?: number;
  semesterName?: string;
}

interface NotesSliceState {
  notes: Note[];
  categories: Category[];
  selectedCategoryId: string | null;
  notesModal: NotesModalData;
}

const getStoredNotes = (): Note[] => {
  const stored = localStorage.getItem(LOCAL_NOTES_KEY);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return parsed.map((note: any) => ({
      ...note,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
      todos: note.todos?.map((todo: any) => ({
        ...todo,
        createdAt: new Date(todo.createdAt),
      })) || [],
    }));
  } catch {
    return [];
  }
};

const getStoredCategories = (): Category[] => {
  const stored = localStorage.getItem(LOCAL_CATEGORIES_KEY);
  if (!stored) return [
    {
      id: "default",
      name: "Khác",
      color: "#6B7280",
      isSemesterCategory: false,
    }
  ];
  try {
    return JSON.parse(stored);
  } catch {
    return [
      {
        id: "default",
        name: "Khác",
        color: "#6B7280",
        isSemesterCategory: false,
      }
    ];
  }
};

const saveNotes = (notes: Note[]) => {
  localStorage.setItem(LOCAL_NOTES_KEY, JSON.stringify(notes));
};

const saveCategories = (categories: Category[]) => {
  localStorage.setItem(LOCAL_CATEGORIES_KEY, JSON.stringify(categories));
};

const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

const initialState: NotesSliceState = {
  notes: getStoredNotes(),
  categories: getStoredCategories(),
  selectedCategoryId: null,
  notesModal: {
    visible: false,
  },
};

const notesSlice = createSlice({
  name: "notes",
  initialState,
  reducers: {
    addNote: (state, action: PayloadAction<NoteRequest>) => {
      const now = new Date();
      const newNote: Note = {
        id: action.payload.id || generateId(),
        createdAt: now,
        updatedAt: now,
        content: action.payload.content,
        color: action.payload.color || "#FBBF24",
        categoryId: action.payload.categoryId,
        isFavorite: action.payload.isFavorite || false,
        todos: action.payload.todos || [],
      };
      state.notes.push(newNote);
      saveNotes(state.notes);
    },

    updateNote: (state, action: PayloadAction<{ id: string; updates: Partial<NoteRequest> }>) => {
      const { id, updates } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === id);
      if (noteIndex !== -1) {
        state.notes[noteIndex] = {
          ...state.notes[noteIndex],
          ...updates,
          updatedAt: new Date(),
        };
        saveNotes(state.notes);
      }
    },

    deleteNote: (state, action: PayloadAction<string>) => {
      state.notes = state.notes.filter(note => note.id !== action.payload);
      saveNotes(state.notes);
    },

    toggleNoteFavorite: (state, action: PayloadAction<string>) => {
      const noteIndex = state.notes.findIndex(note => note.id === action.payload);
      if (noteIndex !== -1) {
        state.notes[noteIndex].isFavorite = !state.notes[noteIndex].isFavorite;
        state.notes[noteIndex].updatedAt = new Date();
        saveNotes(state.notes);
      }
    },

    addTodoToNote: (state, action: PayloadAction<{ noteId: string; todo: Omit<TodoItem, "id" | "createdAt"> }>) => {
      const { noteId, todo } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === noteId);
      if (noteIndex !== -1) {
        const newTodo: TodoItem = {
          id: generateId(),
          createdAt: new Date(),
          ...todo,
        };
        state.notes[noteIndex].todos.push(newTodo);
        state.notes[noteIndex].updatedAt = new Date();
        saveNotes(state.notes);
      }
    },

    updateTodoInNote: (state, action: PayloadAction<{ noteId: string; todoId: string; updates: Partial<TodoItem> }>) => {
      const { noteId, todoId, updates } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === noteId);
      if (noteIndex !== -1) {
        const todoIndex = state.notes[noteIndex].todos.findIndex(todo => todo.id === todoId);
        if (todoIndex !== -1) {
          state.notes[noteIndex].todos[todoIndex] = {
            ...state.notes[noteIndex].todos[todoIndex],
            ...updates,
          };
          state.notes[noteIndex].updatedAt = new Date();
          saveNotes(state.notes);
        }
      }
    },

    deleteTodoFromNote: (state, action: PayloadAction<{ noteId: string; todoId: string }>) => {
      const { noteId, todoId } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === noteId);
      if (noteIndex !== -1) {
        state.notes[noteIndex].todos = state.notes[noteIndex].todos.filter(todo => todo.id !== todoId);
        state.notes[noteIndex].updatedAt = new Date();
        saveNotes(state.notes);
      }
    },

    reorderTodosInNote: (state, action: PayloadAction<{ noteId: string; fromIndex: number; toIndex: number }>) => {
      const { noteId, fromIndex, toIndex } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === noteId);
      if (noteIndex !== -1) {
        const todos = [...state.notes[noteIndex].todos];
        const [movedTodo] = todos.splice(fromIndex, 1);
        todos.splice(toIndex, 0, movedTodo);
        state.notes[noteIndex].todos = todos;
        state.notes[noteIndex].updatedAt = new Date();
        saveNotes(state.notes);
      }
    },

    addCategory: (state, action: PayloadAction<CategoryRequest>) => {
      const newCategory: Category = {
        id: action.payload.id || generateId(),
        name: action.payload.name,
        color: action.payload.color || "#6B7280",
        isSemesterCategory: action.payload.isSemesterCategory || false,
        semesterId: action.payload.semesterId,
      };
      state.categories.push(newCategory);
      saveCategories(state.categories);
    },

    updateCategory: (state, action: PayloadAction<{ id: string; updates: Partial<CategoryRequest> }>) => {
      const { id, updates } = action.payload;
      const categoryIndex = state.categories.findIndex(category => category.id === id);
      if (categoryIndex !== -1) {
        state.categories[categoryIndex] = {
          ...state.categories[categoryIndex],
          ...updates,
        };
        saveCategories(state.categories);
      }
    },

    deleteCategory: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      
      // Move notes from deleted category to "Khác" category
      const defaultCategory = state.categories.find(cat => cat.id === "default");
      if (defaultCategory) {
        state.notes.forEach(note => {
          if (note.categoryId === categoryId) {
            note.categoryId = "default";
            note.updatedAt = new Date();
          }
        });
      }
      
      state.categories = state.categories.filter(category => category.id !== categoryId);
      saveCategories(state.categories);
      saveNotes(state.notes);
    },

    createSemesterCategory: (state, action: PayloadAction<{ semesterId: number; semesterName: string }>) => {
      const { semesterId, semesterName } = action.payload;
      const existingCategory = state.categories.find(
        cat => cat.isSemesterCategory && cat.semesterId === semesterId
      );
      
      if (!existingCategory) {
        const newCategory: Category = {
          id: `semester_${semesterId}`,
          name: semesterName,
          color: "#3B82F6",
          isSemesterCategory: true,
          semesterId,
        };
        state.categories.push(newCategory);
        saveCategories(state.categories);
      }
    },

    handleSemesterDeletion: (state, action: PayloadAction<{ semesterId: number; semesterName: string }>) => {
      const { semesterId, semesterName } = action.payload;
      const semesterCategoryId = `semester_${semesterId}`;
      
      // Create a new category for deleted semester notes
      const deletedCategoryId = `deleted_semester_${semesterId}_${Date.now()}`;
      const deletedCategory: Category = {
        id: deletedCategoryId,
        name: `${semesterName} (đã xóa)`,
        color: "#6B7280",
        isSemesterCategory: false,
        isDeleted: true,
      };
      
      // Move all notes from the semester category to the new deleted category
      state.notes.forEach(note => {
        if (note.categoryId === semesterCategoryId) {
          note.categoryId = deletedCategoryId;
          note.updatedAt = new Date();
        }
      });
      
      // Remove the original semester category and add the deleted category
      state.categories = state.categories.filter(cat => cat.id !== semesterCategoryId);
      state.categories.push(deletedCategory);
      
      saveCategories(state.categories);
      saveNotes(state.notes);
    },

    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
    },

    showNotesModal: (state, action: PayloadAction<{ semesterId?: number; semesterName?: string }>) => {
      state.notesModal = {
        visible: true,
        semesterId: action.payload.semesterId,
        semesterName: action.payload.semesterName,
      };
    },

    hideNotesModal: (state) => {
      state.notesModal.visible = false;
    },
  },
});

export const {
  addNote,
  updateNote,
  deleteNote,
  toggleNoteFavorite,
  addTodoToNote,
  updateTodoInNote,
  deleteTodoFromNote,
  reorderTodosInNote,
  addCategory,
  updateCategory,
  deleteCategory,
  createSemesterCategory,
  handleSemesterDeletion,
  setSelectedCategory,
  showNotesModal,
  hideNotesModal,
} = notesSlice.actions;

export default notesSlice.reducer;